import { Component, inject, OnInit, OnDestroy, signal, computed, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertComponent, ButtonComponent, IconComponent, InputComponent } from 'nebula-ui-kit';
import {
  AuthContent,
  AuthService,
  TranslatePipe,
  TranslationService,
} from '@nebula-shared/data-access';
import { LoginService } from '../../../../data-access/src/lib/login.service';

@Component({
  selector: 'nebula-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    InputComponent,
    ButtonComponent,
    AlertComponent,
    IconComponent,
    TranslatePipe,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly loginService = inject(LoginService);
  private readonly translation = inject(TranslationService);

  // Selector de idioma en el login (pre-sesion). Reusa el i18n; bundle publico.
  readonly languages = this.translation.availableLanguages;
  readonly activeLang = signal<string>(this.translation.currentLang);
  readonly langMenuOpen = signal<boolean>(false);
  readonly activeFlag = computed(() => {
    const lang = this.languages().find((l) => l.code === this.activeLang());
    return lang?.flagEmoji ?? '🌐';
  });

  toggleLangMenu(): void {
    this.langMenuOpen.update((v) => !v);
  }

  async selectLanguage(code: string): Promise<void> {
    this.langMenuOpen.set(false);
    await this.translation.setLanguage(code);
    this.activeLang.set(code);
  }
  private readonly router = inject(Router);
  readonly resizeListener: () => void;
  readonly renderer = inject(Renderer2);

  form!: FormGroup;
  hidePassword = true;
  loading = signal(false);
  error = signal<{ TITLE: string; SUBTITLE: string } | null>(null);
  isDarkMode = signal<boolean>(false);
  isMobile = false;
  isTablet = false;

  constructor() {
    this.checkScreenSize();
    this.resizeListener = this.checkScreenSize.bind(this);
    this.form = this.fb.group({
      user: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeListener);
    }
    const savedDarkMode = this.havesaveDarkMode();
    this.isDarkMode.set(savedDarkMode);
    this.applyTheme(savedDarkMode);
  }

  private havesaveDarkMode(): boolean {
    const darkMode = localStorage.getItem('dark_mode');
    return darkMode === 'true';
  }
  private applyTheme(isDark: boolean): void {
    const htmlElement = document.documentElement;

    if (isDark) {
      this.renderer.addClass(htmlElement, 'dark-theme');
    } else {
      this.renderer.removeClass(htmlElement, 'dark-theme');
    }
  }

  private showError(error: string) {
    this.error.set(null);

    setTimeout(() => {
      this.error.set({
        TITLE: this.translation.translate('LOGIN.ERR_AUTH_TITLE'),
        SUBTITLE: this.getErrorMessage(error),
      });
    });
  }
  private getErrorMessage(error: string): string {
    if (error) return error;
    return '';
  }

  private checkScreenSize(): void {
    const width = window.innerWidth;
    this.isMobile = width <= 768;
    this.isTablet = width > 768 && width <= 1024;
  }

  getError(key: string) {
    const control = this.form.get(key);

    if (!control || !control.touched) return;

    if (control.errors?.['required']) return this.translation.translate('LOGIN.REQUIRED');
    return '';
  }

  onSubmit(): void {
    this.error.set(null);

    if (this.form.valid) {
      this.loading.set(true);

      const { user, password } = this.form.value;
      const payload = {
        user: user.trim().toLowerCase(),
        password: password.trim(),
      };

      this.loginService.login(payload).subscribe({
        next: (content: AuthContent) => {
          // Contraseña vencida: el login es válido, pero se fuerza el cambio antes de entrar.
          if (content?.mustChangePassword) {
            this.auth.setMustChangePassword(true);
            this.loading.set(false);
            this.router.navigate(['/cambiar-clave']);
            return;
          }
          this.auth.setMustChangePassword(false);
          this.auth.validateTokenAndGetSession().subscribe({
            next: () => {
              this.loading.set(false);
              this.router.navigate(['/layout/company']);
            },
            error: (e: any) => {
              this.loading.set(false);
              this.showError(e);
            },
          });
        },
        error: (err: string) => {
          this.loading.set(false);
          this.showError(err);
        },
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  toggleDarkMode(): void {
    const newDarkMode = !this.isDarkMode();
    this.isDarkMode.set(newDarkMode);
    localStorage.setItem('dark_mode', newDarkMode.toString());
    this.applyTheme(newDarkMode);
  }

  /** Navega a la página pública de recuperación de contraseña ("¿Olvidaste tu clave?"). */
  irARecuperar(): void {
    this.router.navigate(['/recuperar']);
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
}
